/**
 * ShareSupplyPlanModal — Feb 2026 mid-month.
 *
 * Small dialog opened from the /supply-plans/deliver page. Pre-fills a
 * colleague's email into the same free supply-plan flow, deduped
 * against the seeded library so cost stays $0. Turns each claim into
 * ~1.3 claims on average.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CheckCircle2, Loader2, Send, X } from "lucide-react";
import { shareSupplyPlan } from "../lib/supplyPlanFreeApi";

export default function ShareSupplyPlanModal({ open, onOpenChange, token, grade }) {
  const [colleagueEmail, setColleagueEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { status, colleague_email }

  const reset = () => {
    setColleagueEmail("");
    setNote("");
    setError("");
    setResult(null);
    setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = colleagueEmail.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const data = await shareSupplyPlan({
        token,
        colleagueEmail: trimmed,
        note: note.trim().slice(0, 240) || null,
      });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg = detail || "Something went wrong — try again in a moment.";
      if (Array.isArray(detail)) msg = detail[0]?.msg || "Invalid input.";
      if (status === 429) msg = detail || "This plan has already been shared with the maximum number of colleagues.";
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const shareAgain = () => {
    setResult(null);
    setColleagueEmail("");
    setNote("");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="supply-plan-share-modal"
        className="sm:max-w-md bg-white"
      >
        <DialogHeader>
          <DialogTitle className="font-black text-xl text-[#1c2d3d]">
            Share with a colleague
          </DialogTitle>
          <DialogDescription className="text-sm text-[#4a6984]">
            We&rsquo;ll email them the same {grade} plan (attached as a PDF). Free
            for them, no signup required.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="mt-2 space-y-4" data-testid="supply-plan-share-success">
            <div className="flex items-start gap-3 rounded-md bg-[#96ded1]/25 border border-[#96ded1]/50 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-[#1c2d3d] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-extrabold text-[#1c2d3d]">
                  {result.status === "already_shared" ? "Already sent" : "Sent!"}
                </p>
                <p className="text-sm text-[#4a6984] mt-1">
                  {result.status === "already_shared"
                    ? `${result.colleague_email} was already sent this plan.`
                    : `We emailed ${result.colleague_email} the plan and PDF.`}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={shareAgain}
                data-testid="supply-plan-share-again-btn"
                className="text-sm font-bold text-[#2a7d6b] hover:text-[#1c5a4e] transition"
              >
                Share with another colleague
              </button>
              <button
                type="button"
                onClick={() => handleClose(false)}
                data-testid="supply-plan-share-done-btn"
                className="rounded-md bg-[#1c2d3d] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-2 space-y-4">
            <div>
              <label
                htmlFor="colleague-email"
                className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]"
              >
                Colleague&rsquo;s email
              </label>
              <input
                id="colleague-email"
                type="email"
                required
                value={colleagueEmail}
                onChange={(e) => setColleagueEmail(e.target.value)}
                placeholder="colleague@school.ca"
                data-testid="supply-plan-share-email-input"
                className="mt-1 w-full rounded-md border border-[#4a6984]/25 px-3 py-2 text-sm text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1] focus:border-[#96ded1]"
              />
            </div>
            <div>
              <label
                htmlFor="share-note"
                className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]"
              >
                Short note <span className="normal-case font-medium text-[#4a6984]/60">(optional)</span>
              </label>
              <textarea
                id="share-note"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 240))}
                placeholder="Thought you'd want this for tomorrow…"
                maxLength={240}
                rows={2}
                data-testid="supply-plan-share-note-input"
                className="mt-1 w-full rounded-md border border-[#4a6984]/25 px-3 py-2 text-sm text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1] focus:border-[#96ded1] resize-none"
              />
              <p className="mt-1 text-[10px] text-[#4a6984]/60 text-right tabular-nums">
                {note.length}/240
              </p>
            </div>
            {error && (
              <p
                data-testid="supply-plan-share-error"
                className="text-sm text-[#b85b5b] bg-[#fdf3f3] rounded-md px-3 py-2"
              >
                {error}
              </p>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => handleClose(false)}
                data-testid="supply-plan-share-cancel-btn"
                className="rounded-md px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:bg-[#f4f5f7]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !colleagueEmail.trim()}
                data-testid="supply-plan-share-submit-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Send plan
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
